import { IExchangeSchedule } from '../../../api/vault/exchangeSchedule';
import { IVaultTeam } from '../../../api/vault/teams';
import {
  IExchangeFile, setFileUploadComment, setFileUploadQualifier, setFileUploadVideoAngle,
} from '../../../state/exchange/uploadDialog';
import { ISystemSetting } from '../../../api/vault/systemSettings';
import { SelectOptionType } from '../../../utils/selection';
import {
  ILookupOption,
  LookupName,
  updateLookupByName,
  selectExchangeGameTypeOptions,
  selectExchangeQualifiersOptions,
  selectExchangeSportOptions,
  selectLeagueOptions,
  optionsToReactSelect,
  selectExchangeVideoAngleOptions,
} from '../../../state/lookups';
import {
  queryExchangeSchedules,
  queryTeamsForCreate,
  updateSelectedSeason,
  updateSelectedSchedule,
  updateSelectedSport,
  updateSelectedLeague,
  updateSelectedExchangeQualifier,
  updateGameDate,
  updateHomeGameNumber,
  updateVisitorGameNumber,
  updateHomeTeam,
  updateAwayTeam,
  updateUploader,
  updateScoutedTeam,
  toggleIsActive,
  updateGameId,
  selectTeamOptions,
  updateSelectedExchangeGameType,
  clearSelectedTeams,
  updateSelectedExchangeVideoAngle,
  FormType,
  IconMap,
  ITeamOption,
  selectUploadSport,
} from '../../../state/exchange/gameScheduleForm';
import { IAppState } from '../../../state/reducers';
import { getLocale } from '../../../state/localization';
import { LocalizationLabels } from '../../../enums';

export interface ITeamData {
  teamIcons: IconMap;
  source: IVaultTeam;
}

export interface IOwnProps {
  isSeasonSelectable?: boolean;
  formHeading?: JSX.Element;
}

interface IStateProps {
  formType: FormType;
  isLoaded: boolean;
  isError: boolean;
  seasons: string[];
  teamIcons: IconMap;
  exchangeSchedules: IExchangeSchedule[];
  selectedSeason: string;
  selectedSchedule: IExchangeSchedule;
  selectedSport: string;
  selectedLeague: string;
  selectedUploadFiles: IExchangeFile[];
  selectedExchangeQualifier: string;
  selectedExchangeVideoAngle: string;
  selectedExchangeGameType: string;
  gameDate: string;
  homeGameNumber: string;
  visitorGameNumber: string;
  isActive: boolean;
  homeTeam: IVaultTeam;
  awayTeam: IVaultTeam;
  sourceTeam: IVaultTeam;
  scoutedTeam: IVaultTeam;
  exchangeQualifierOptions: SelectOptionType<string>[];
  exchangeVideoAngleOptions: ILookupOption[];
  exchangeSportOptions: ILookupOption[];
  exchangeGameTypeOptions: ILookupOption[];
  leagueOptions: ILookupOption[];
  isConference: boolean;
  teamOptions: ITeamOption[];
  strictTeamOptions: ITeamOption[];
  uploader: ITeamOption;
  scoutedTeamOption: ITeamOption;
  gameId: string;
  phaseLabel: string;
  sport: string;
  // The user's own product sport, for comparison against the game's sport
  productSport: string;
  loggedInUsersTeam: IVaultTeam;
  systemSettings: ISystemSetting;
}

export const mapStateToProps = (state: IAppState): IStateProps => ({
  formType: state.exchange.gameScheduleForm.formType,
  isLoaded: state.exchange.gameScheduleForm.isLoaded,
  isError: state.exchange.gameScheduleForm.isError,
  seasons: state.exchange.gameScheduleForm.seasons,
  teamIcons: state.exchange.gameScheduleForm.teamIcons,
  exchangeSchedules: state.exchange.gameScheduleForm.exchangeSchedules,
  selectedSeason: state.exchange.gameScheduleForm.selectedSeason,
  selectedSchedule: state.exchange.gameScheduleForm.selectedSchedule,
  selectedSport: state.exchange.gameScheduleForm.selectedSport,
  selectedLeague: state.exchange.gameScheduleForm.selectedLeague,
  selectedUploadFiles: state.exchange.uploadDialog.selectedFiles,
  selectedExchangeQualifier: state.exchange.gameScheduleForm.selectedExchangeQualifier,
  selectedExchangeVideoAngle: state.exchange.gameScheduleForm.selectedExchangeVideoAngle,
  selectedExchangeGameType: state.exchange.gameScheduleForm.selectedExchangeGameType,
  gameDate: state.exchange.gameScheduleForm.gameDate,
  homeGameNumber: state.exchange.gameScheduleForm.homeGameNumber,
  visitorGameNumber: state.exchange.gameScheduleForm.visitorGameNumber,
  isActive: state.exchange.gameScheduleForm.isActive,
  homeTeam: state.exchange.gameScheduleForm.homeTeam,
  awayTeam: state.exchange.gameScheduleForm.awayTeam,
  sourceTeam: state.exchange.gameScheduleForm.sourceTeam,
  scoutedTeam: state.exchange.gameScheduleForm.scoutedTeam,
  exchangeQualifierOptions: optionsToReactSelect(selectExchangeQualifiersOptions(state)),
  exchangeVideoAngleOptions: selectExchangeVideoAngleOptions(state),
  exchangeSportOptions: selectExchangeSportOptions(state),
  exchangeGameTypeOptions: selectExchangeGameTypeOptions(state),
  leagueOptions: selectLeagueOptions(state),
  isConference: state.exchange.gameScheduleForm.isConference,
  teamOptions: selectTeamOptions(state),
  strictTeamOptions: selectTeamOptions(state, true),
  uploader: state.exchange.gameScheduleForm.uploader,
  scoutedTeamOption: state.exchange.gameScheduleForm.scoutedTeamOption,
  gameId: state.exchange.gameScheduleForm.gameId ? state.exchange.gameScheduleForm.gameId : '',
  phaseLabel: getLocale(state, LocalizationLabels.ExchangePhase),
  // While uploading, form rules follow the game being uploaded to. The create/update
  // schedule dialogs keep using the user's own product sport.
  sport: state.exchange.gameScheduleForm.formType === FormType.Upload
    ? selectUploadSport(state)
    : state.exchange.initExchange.sport,
  productSport: state.exchange.initExchange.sport,
  loggedInUsersTeam: state.exchange.initExchange.currentTeam,
  systemSettings: state.systemSettings?.systemSettings,
});

interface IDispatchProps {
  onQueryExchangeSchedules: (upcoming: boolean, canUploadNewSchedule: boolean, isUpload: boolean) => void;
  onQueryTeamsForCreate: () => void;
  onSelectSeason: (season: string) => void;
  onSelectSchedule: (schedule: IExchangeSchedule) => void;
  setFileUploadQualifier: (index: number, qualifier: string) => void;
  setFileUploadVideoAngle: (index: number, videoAngle: string) => void;
  setFileUploadComment: (index: number, comment: string) => void;
  onSelectExchangeQualifier: (exchangeQualifier: string) => void;
  onSelectExchangeVideoAngle: (videoAngle: string) => void;
  onSelectExchangeGameType: (gameType: string) => void;
  onSelectSport: (selectedSport: string) => void;
  onSelectLeague: (selectedLeague: string) => void;
  onUpdateGameDate: (gameDate: string) => void;
  onUpdateHomeGameNumber: (homeGameNumber: string) => void;
  onUpdateVisitorGameNumber: (visitorGameNumber: string) => void;
  onToggleIsActive: () => void;
  onUpdateHomeTeam: (homeTeam: ITeamOption) => void;
  onUpdateAwayTeam: (awayTeam: ITeamOption) => void;
  onUpdateUploader: (uploader: ITeamOption) => void;
  onUpdateQualifierLookup: (sport: string) => void;
  onUpdateScoutedTeam: (scoutedTeamOption: ITeamOption) => void;
  onUpdateGameId: (gameId: string) => void;
  onClearTeams: () => void;
}

export const mapDispatchToProps: IDispatchProps = {
  onQueryExchangeSchedules: queryExchangeSchedules,
  onQueryTeamsForCreate: queryTeamsForCreate,
  onSelectSeason: updateSelectedSeason,
  onSelectSchedule: updateSelectedSchedule,
  onSelectSport: updateSelectedSport,
  onSelectLeague: updateSelectedLeague,
  setFileUploadQualifier,
  setFileUploadVideoAngle,
  setFileUploadComment,
  onSelectExchangeQualifier: updateSelectedExchangeQualifier,
  onSelectExchangeVideoAngle: updateSelectedExchangeVideoAngle,
  onSelectExchangeGameType: updateSelectedExchangeGameType,
  onUpdateGameDate: updateGameDate,
  onUpdateHomeGameNumber: updateHomeGameNumber,
  onUpdateVisitorGameNumber: updateVisitorGameNumber,
  onUpdateHomeTeam: updateHomeTeam,
  onUpdateAwayTeam: updateAwayTeam,
  onUpdateUploader: updateUploader,
  // Qualifier values are served per sport by the API (hockey's come from the period lookup)
  onUpdateQualifierLookup: (sport: string) => updateLookupByName(LookupName.ExchangeQualifier, sport),
  onUpdateScoutedTeam: updateScoutedTeam,
  onToggleIsActive: toggleIsActive,
  onUpdateGameId: updateGameId,
  onClearTeams: clearSelectedTeams,
};

export type IProps = IOwnProps & IStateProps & IDispatchProps;

export type IValueOption = SelectOptionType<string>;
export type IScheduleOption = SelectOptionType<IExchangeSchedule> & {
  teamData: ITeamData;
};
export type IExchangeQualifierOption = SelectOptionType<string>;

export interface IGameIdFieldProps {
  isEditable: boolean;
  gameId: string;
  selectedSeason: string;
  selectedSport: string;
  selectedLeague: string;
  onUpdateGameId: (gameId: string) => void;
  onGetGameId: () => Promise<void>;
}
